import Modal from "common/Modal/Modal";
import Input, { inputStyles, SharedInputProps } from "common/Inputs/Input";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import RangePicker from "common/DatePicker/RangePicker";
import styled, { css, useTheme } from "styled-components";
import { ErrorText } from "features/bots/common/styles";
import Button, { buttonStyles } from "common/Buttons/Button";
import useBacktestModal from "@shared/features/bots/BotForm/Backtesting/useBacktestModal";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import React from "react";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import Col from "antd/es/col";
import InputNumber from "antd/es/input-number";
import Row from "antd/es/row";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { displayResultMessage } from "utils/misc";
import { typography } from "@shared/common/Themes/typography";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import AmountSelector from "features/bots/BotForm/General/AmountSelector";
import StartGroupFreeTrialModal from "features/bots/BotForm/common/StartGroupFreeTrialModal";
import { getBotFormBacktestingRoute } from "utils/routes";
import { useHistory } from "react-router-dom";
import { BacktestingRunType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface BacktestModalProps {
  open: boolean;
  closeModal: () => void;
}

const BacktestModal = ({ open, closeModal }: BacktestModalProps) => {
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const history = useHistory();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const onStartBacktesting = (response: BacktestingRunType) => {
    history.push(
      getBotFormBacktestingRoute(
        location.pathname,
        response.bot_id,
        response.id
      )
    );
  };

  const {
    nameData,
    timeframeData,
    initialFundsData,
    footerData,
    destroyOnClose,
    backtestHelperProps,
    equivalentAmount,
    freeTrialModalData,
    isSignalsBot,
  } = useBacktestModal({
    closeModal,
    displayResultMessage,
    onStartBacktesting,
  });

  // New look: the usage as a quiet meta line on the left, the actions on the
  // right (one primary).
  const limit = footerData.backtestingLimit;
  const newLookFooter = (
    <>
      {footerData.showError && (
        <NewLookError style={{ marginBottom: 8 }}>
          {footerData.error}
        </NewLookError>
      )}
      <NewLookFooterRow>
        {limit !== undefined && limit !== null && (
          <NewLookUsage>
            {t("backtesting.modal.monthly_backtesting_runs")}{" "}
            <strong>
              {footerData.backtestingUsage || 0} /{" "}
              {String(limit) === "inf" ? "∞" : limit}
            </strong>
          </NewLookUsage>
        )}
        <NewLookActions>
          <Button onClick={footerData.onCancel}>
            {footerData.cancelButton}
          </Button>
          <OkButton
            type="primary"
            onClick={footerData.onOk}
            loading={footerData.okLoading}
            $disabled={footerData.okDisabled}
          >
            {footerData.okButton}
          </OkButton>
        </NewLookActions>
      </NewLookFooterRow>
    </>
  );

  const footer = theme.isNewLook ? (
    newLookFooter
  ) : (
    <>
      {footerData.showError && (
        <ErrorText style={{ marginBottom: 4 }}>{footerData.error}</ErrorText>
      )}
      <Flex
        style={
          theme.isNewLook && !footerData.backtestingLimit
            ? { justifyContent: "flex-end" }
            : undefined
        }
      >
        {footerData.backtestingLimit && (
          <Text
            type="secondary"
            $size="S"
            style={{ textAlign: "left", marginRight: 8 }}
          >
            {t("backtesting.modal.monthly_backtesting_runs")}:{" "}
            <span style={{ fontSize: typography.pxFontSizes.M }}>
              {footerData.backtestingUsage || 0}
            </span>
            /{footerData.backtestingLimit}
          </Text>
        )}
        <Flex $justifyContent="flex-end" style={{ columnGap: 8 }}>
          <Button onClick={footerData.onCancel}>
            {footerData.cancelButton}
          </Button>
          <OkButton
            type="primary"
            onClick={footerData.onOk}
            loading={footerData.okLoading}
            $disabled={footerData.okDisabled}
          >
            {footerData.okButton}
          </OkButton>
        </Flex>
      </Flex>
    </>
  );

  const title = theme.isNewLook ? (
    <NewLookTitle>
      {t("backtesting.modal.title")}
      <HelperTooltip {...backtestHelperProps} />
    </NewLookTitle>
  ) : (
    <Title level={3}>
      {t("backtesting.modal.title")}
      <HelperTooltip {...backtestHelperProps} />
    </Title>
  );

  const getInitialFundsInputs = () => {
    return (
      <Row gutter={[16, 16]}>
        {initialFundsData.data?.map(([coin, signals]) => {
          return (
            <Col span={12} key={coin}>
              {theme.isNewLook ? (
                <NewLookCoinLabel>
                  <TextWithIcon
                    iconType={ICON_TYPES.COIN}
                    coinOrExchangeName={coin}
                    text={coin}
                    textStyle={{ color: "inherit", marginRight: 0 }}
                  />
                  {isSignalsBot && (
                    <NewLookMeta>
                      {signals} {t("backtesting.modal.signals")}
                    </NewLookMeta>
                  )}
                </NewLookCoinLabel>
              ) : (
                <Flex $justifyContent="flex-start" style={{ marginBottom: 8 }}>
                  <TextWithIcon
                    iconType={ICON_TYPES.COIN}
                    coinOrExchangeName={coin}
                    text={coin}
                    textStyle={{
                      color: theme.colors.primaryText,
                      marginRight: 4,
                    }}
                  />
                  {isSignalsBot && (
                    <Text type="secondary">
                      ({signals} {t("backtesting.modal.signals")})
                    </Text>
                  )}
                </Flex>
              )}
              <StyledInputNumber
                value={initialFundsData.getValue(coin)}
                controls={false}
                onChange={(value) => {
                  initialFundsData.onChange(
                    coin,
                    value ? parseFloat(value.toString()) : 0
                  );
                }}
              />
            </Col>
          );
        })}
      </Row>
    );
  };

  const getInitialFunds = () => {
    if (!initialFundsData.show) return;
    return (
      <Item>
        <ItemTitle>
          {initialFundsData.title}
          <HelperTooltip {...initialFundsData.helperProps} />
        </ItemTitle>
        <LoadingSpinnerOverlay
          isLoading={initialFundsData.loading}
          wrap={true}
          hideContentWhileLoading={true}
        >
          {initialFundsData.showEmpty ? (
            <ErrorText style={{ fontSize: typography.fontSizes.M }}>
              {initialFundsData.emptyText}
            </ErrorText>
          ) : (
            getInitialFundsInputs()
          )}
        </LoadingSpinnerOverlay>
        {!initialFundsData.showEmpty && initialFundsData.error && (
          <ErrorText style={{ marginTop: 8 }}>
            {initialFundsData.error}
          </ErrorText>
        )}
      </Item>
    );
  };

  return (
    <>
      <Modal
        title={title}
        open={open}
        onCancel={closeModal}
        footer={footer}
        destroyOnHidden={destroyOnClose}
        styles={{
          body: {
            padding: theme.isNewLook
              ? isMobile
                ? "12px 16px 16px"
                : "12px 24px 20px"
              : isMobile
              ? 16
              : 24,
          },
        }}
      >
        <ItemsWrapper>
          <Item>
            <ItemTitle>{nameData.title}</ItemTitle>
            <Input
              value={nameData.value}
              onChange={(e) => nameData.onChange(e.target.value)}
            />
            <ErrorText>{nameData.error}</ErrorText>
          </Item>
          <Item>
            <ItemTitle>{timeframeData.title}</ItemTitle>
            <RangePicker
              value={timeframeData.value}
              onChange={timeframeData.onChange}
              wrapperStyle={{ width: "100%" }}
              onPresetsClick={timeframeData.onChange}
              minValue={timeframeData.minValue}
              maxValue={timeframeData.maxValue}
            />
            <ErrorText>{timeframeData.error}</ErrorText>
          </Item>
          {getInitialFunds()}
          {initialFundsData.show && (
            <Item>
              <AmountSelector
                titleStyle={
                  theme.isNewLook
                    ? NEW_LOOK_LABEL_STYLE(getNewLookTokens(theme).secondary)
                    : { fontSize: typography.fontSizes.M }
                }
                hideErrors={isSignalsBot}
                isBacktestModal={true}
              />
              {theme.isNewLook ? (
                <NewLookMeta as="div" style={{ marginTop: 6 }}>
                  {equivalentAmount}
                </NewLookMeta>
              ) : (
                <Text type="secondary" style={{ marginTop: 8 }}>
                  {equivalentAmount}
                </Text>
              )}
            </Item>
          )}
        </ItemsWrapper>
      </Modal>
      <StartGroupFreeTrialModal
        isOpen={freeTrialModalData.isOpen}
        closeModal={freeTrialModalData.onCancel}
        saveChanges={freeTrialModalData.saveChanges}
      />
    </>
  );
};

export default BacktestModal;

const ItemsWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  row-gap: ${(p) => (p.theme.isNewLook ? 20 : 24)}px;
`;
const Item = styled.div`
  width: 100%;
`;
const ItemTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin-bottom: 6px;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `};
`;
const OkButton = styled(Button)<{ $disabled?: boolean }>`
  ${(p) =>
    p.$disabled &&
    `
      ${buttonStyles.antBtnDisabled};
      cursor: auto;
    `};
`;
const StyledInputNumber = styled(InputNumber)<SharedInputProps>`
  width: 100%;
  ${inputStyles};
  &.ant-input-number .ant-input-number-input {
    color: ${(p) => p.theme.colors.primaryText};
  }
`;

/* New look */
const NEW_LOOK_LABEL_STYLE = (color: string) => ({
  fontSize: 12.5,
  lineHeight: "16px",
  fontWeight: 500,
  color,
});
const NewLookTitle = styled.span`
  display: inline-flex;
  align-items: center;
`;
const NewLookCoinLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookError = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).down};
  text-align: left;
`;
const NewLookFooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
`;
const NewLookUsage = styled.span`
  margin-right: auto;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: left;
  strong {
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
const NewLookActions = styled.div`
  display: flex;
  gap: 8px;
`;
